import { DELIMITER, NULL } from '../../helpers/priceTable/constants';
import { Control, useWatch } from 'react-hook-form';

// Shaped to match @weareopenr/common's `Price`, since these objects are fed straight
// into applyPriceRules. `value_with_tax` widens to null because an empty input has no value.
interface IFallbackPrice {
  price_id: string;
  price_band_id: string | null;
  extension_instance_id: string | null;
  tax_rate: number;
  value_with_tax: number | null;
}

interface IFallbackPrices {
  basePrice: IFallbackPrice;
  priceBandDefault: IFallbackPrice | null;
  extensionInstanceDefault: IFallbackPrice | null;
  inputValue: IFallbackPrice;
}

const parseWatchReturn = (value: string): number | null => {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? null : parsedValue;
};

/***
 Returns an object of prices that are important to a specified field, including its own value, within a pricing form
 The price IDS are form specific. For more information the structure of ids, see the PriceManagement component
 Example return:
 @return {IFallbackPrices[]} Object containing base price, price band default, extension instance default, and input value
 * @param itemId
 * @param extensionInstanceId
 * @param priceBandId
 * @param control
 */
export const useFormFallbackPrices = (
  itemId: string,
  extensionInstanceId: string,
  priceBandId: string,
  control: Control,
): IFallbackPrices => {
  const priceBandDefaultId = `${itemId}${DELIMITER}null${DELIMITER}${priceBandId}`;
  const extensionInstanceDefaultId = `${itemId}${DELIMITER}${extensionInstanceId}${DELIMITER}null`;
  const basePriceId = `${itemId}${DELIMITER}null${DELIMITER}null`;
  const inputName = `${itemId}${DELIMITER}${extensionInstanceId}${DELIMITER}${priceBandId}`;
  const ids = [basePriceId, extensionInstanceDefaultId, priceBandDefaultId, inputName];
  const [watchedBasePrice, watchedExtensionInstanceDefault, watchedPriceBandDefault, watchedInputPrice] = useWatch({
    control,
    name: ids,
  });
  const inputValue: IFallbackPrice = {
    extension_instance_id: extensionInstanceId === NULL ? null : extensionInstanceId,
    price_band_id: priceBandId === NULL ? null : priceBandId,
    price_id: inputName,
    tax_rate: 0,
    value_with_tax: parseWatchReturn(watchedInputPrice),
  };
  const basePrice: IFallbackPrice = {
    price_id: basePriceId,
    extension_instance_id: null,
    price_band_id: null,
    value_with_tax: parseWatchReturn(watchedBasePrice) ?? null,
    tax_rate: 0,
  };

  if (extensionInstanceId !== NULL && priceBandId !== NULL) {
    const priceBandDefault: IFallbackPrice = {
      price_id: priceBandDefaultId,
      extension_instance_id: null,
      price_band_id: priceBandId,
      value_with_tax: parseWatchReturn(watchedPriceBandDefault) ?? null,
      tax_rate: 0,
    };
    const extensionInstanceDefault: IFallbackPrice = {
      price_id: extensionInstanceDefaultId,
      extension_instance_id: extensionInstanceId,
      price_band_id: null,
      value_with_tax: parseWatchReturn(watchedExtensionInstanceDefault) ?? null,
      tax_rate: 0,
    };
    return { basePrice, priceBandDefault, extensionInstanceDefault, inputValue: inputValue };
  }

  return { basePrice, priceBandDefault: null, extensionInstanceDefault: null, inputValue };
};
